import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, UncontrolledTooltip } from "reactstrap";

/**
 * Badge colors for each tax referrer type.
 * Includes both backend types (SPANISH_AUTONOMO_EMPRESARIAL/PROFESIONAL) and
 * frontend consolidated type (SPANISH_AUTONOMO).
 */
const TYPE_COLORS = {
  SPANISH_COMPANY: "primary",
  SPANISH_AUTONOMO: "info",
  SPANISH_AUTONOMO_EMPRESARIAL: "info",
  SPANISH_AUTONOMO_PROFESIONAL: "info",
  SPANISH_PARTICULAR: "warning",
  EU_BUSINESS: "success",
  EU_PRIVATE: "secondary",
  NON_EU_BUSINESS: "dark",
  NON_EU_PRIVATE: "secondary",
};

/**
 * Short labels for each tax referrer type.
 */
const TYPE_SHORT_LABELS = {
  SPANISH_COMPANY: "ES Company",
  SPANISH_AUTONOMO: "ES Autónomo",
  SPANISH_AUTONOMO_EMPRESARIAL: "ES Autónomo (E)",
  SPANISH_AUTONOMO_PROFESIONAL: "ES Autónomo (P)",
  SPANISH_PARTICULAR: "ES Particular",
  EU_BUSINESS: "EU Business",
  EU_PRIVATE: "EU Private",
  NON_EU_BUSINESS: "Non-EU Business",
  NON_EU_PRIVATE: "Non-EU Private",
};

/**
 * Icons for each tax referrer type.
 */
const TYPE_ICONS = {
  SPANISH_COMPANY: "fas fa-building",
  SPANISH_AUTONOMO: "fas fa-briefcase",
  SPANISH_AUTONOMO_EMPRESARIAL: "fas fa-store",
  SPANISH_AUTONOMO_PROFESIONAL: "fas fa-user-tie",
  SPANISH_PARTICULAR: "fas fa-user",
  EU_BUSINESS: "fas fa-globe-europe",
  EU_PRIVATE: "fas fa-user",
  NON_EU_BUSINESS: "fas fa-globe",
  NON_EU_PRIVATE: "fas fa-user",
};

/**
 * TaxReferrerTypeBadge component.
 * Displays a colored badge for the tax referrer type.
 */
const textColorMap = {
  primary: "text-primary",
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  secondary: "text-secondary",
  dark: "text-dark",
  light: "text-muted",
};

// Record views for safe string indexing
const typeColorsRecord: Record<string, string | undefined> = TYPE_COLORS;
const typeIconsRecord: Record<string, string | undefined> = TYPE_ICONS;
const typeShortLabelsRecord: Record<string, string | undefined> = TYPE_SHORT_LABELS;
const textColorMapRecord: Record<string, string | undefined> = textColorMap;

interface TaxReferrerTypeBadgeProps {
  type: string;
  showIcon?: boolean;
  showTooltip?: boolean;
  id?: string;
  size?: "sm" | "md";
}

const TaxReferrerTypeBadge = ({
  type,
  showIcon = true,
  showTooltip = true,
  id,
  size = "md",
}: TaxReferrerTypeBadgeProps) => {
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile.referrerTypes" });
  const badgeId = id || `tax-type-badge-${type}-${Math.random().toString(36).substr(2, 9)}`;

  if (!type) {
    return (
      <Badge color="light" className={size === "sm" ? "badge-sm" : ""}>
        <i className="fas fa-question-circle mr-1" />
        {t("unknown", "Unknown")}
      </Badge>
    );
  }

  const color = typeColorsRecord[type] || "secondary";
  const icon = typeIconsRecord[type] || "fas fa-question-circle";
  const shortLabel = typeShortLabelsRecord[type] || type;
  const fullLabel = t(type, type.replace(/_/g, " "));

  return (
    <>
      <Badge id={badgeId} color={color} className={size === "sm" ? "badge-sm" : ""} pill>
        {showIcon && <i className={`${icon} mr-1`} />}
        {shortLabel}
      </Badge>
      {showTooltip && (
        <UncontrolledTooltip target={badgeId} placement="top">
          {fullLabel}
        </UncontrolledTooltip>
      )}
    </>
  );
};

/**
 * Compact icon-only version for tables.
 */
interface TaxReferrerTypeIconProps {
  type: string;
  id?: string;
}

export const TaxReferrerTypeIcon = ({ type, id }: TaxReferrerTypeIconProps) => {
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile.referrerTypes" });
  const iconId = id || `tax-type-icon-${type}-${Math.random().toString(36).substr(2, 9)}`;

  if (!type) {
    return <i className="fas fa-question-circle text-muted" />;
  }

  const icon = typeIconsRecord[type] || "fas fa-question-circle";
  const color = typeColorsRecord[type] || "secondary";
  const fullLabel = t(type, type.replace(/_/g, " "));

  return (
    <>
      <i id={iconId} className={`${icon} ${textColorMapRecord[color] || "text-muted"}`} style={{ cursor: "help" }} />
      <UncontrolledTooltip target={iconId} placement="top">
        {fullLabel}
      </UncontrolledTooltip>
    </>
  );
};

export default TaxReferrerTypeBadge;
